// src/components/Atelier 2/Activite1/CategoryEditorModal.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CUSTOM_COLOR_SWATCHES } from './ThreatCategories';
import { SYMBOLS_UNIQUE } from './symbolCatalog';

// ponytail: no form library, no validation lib. Inline state + a single guard on submit.
const CategoryEditorModal = ({ isOpen, onClose, onSave, initial }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(initial);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState(SYMBOLS_UNIQUE[0]);
  const [color, setColor] = useState(CUSTOM_COLOR_SWATCHES[0].hex);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(initial?.name || '');
    setDescription(initial?.description || '');
    setIcon(initial?.icon || SYMBOLS_UNIQUE[0]);
    setColor(initial?.color || CUSTOM_COLOR_SWATCHES[0].hex);
    setError('');
  }, [isOpen, initial]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('workshop2.activity1.categoryManager.errors.nameRequired'));
      return;
    }
    onSave({
      name: name.trim(),
      description: description.trim(),
      icon,
      color
    });
  };

  // ponytail: flat grid of symbols rendered inline in the modal. No popover, no search, no groups.
  // User scrolls. Click one. Done. Grouping/search is a YAGNI add when there are signs of need.
  const renderSymbolGrid = () => (
    <div className="border border-gray-300 rounded-md p-2 max-h-32 overflow-y-auto">
      <div className="grid grid-cols-8 gap-1">
        {SYMBOLS_UNIQUE.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setIcon(s)}
            className={`w-8 h-8 rounded flex items-center justify-center text-xl transition-colors ${icon === s ? 'bg-indigo-100 ring-2 ring-indigo-500' : 'hover:bg-gray-100'}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit
              ? t('workshop2.activity1.categoryManager.editTitle')
              : t('workshop2.activity1.categoryManager.addTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('workshop2.activity1.categoryManager.nameLabel')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('workshop2.activity1.categoryManager.descriptionLabel')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('workshop2.activity1.categoryManager.symbolLabel')}
              </label>
              {renderSymbolGrid()}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('workshop2.activity1.categoryManager.colorLabel')}
              </label>
              <div className="flex flex-wrap gap-1.5 p-1.5 border border-gray-300 rounded-md">
                {CUSTOM_COLOR_SWATCHES.map((s) => (
                  <button
                    key={s.hex}
                    type="button"
                    onClick={() => setColor(s.hex)}
                    className={`w-6 h-6 rounded-full transition-transform ${color === s.hex ? 'ring-2 ring-offset-2 ring-gray-700 scale-110' : 'hover:scale-110'}`}
                    style={{ backgroundColor: s.hex }}
                    aria-label={s.name}
                  />
                ))}
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              {t('workshop2.activity1.categoryManager.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
            >
              <Save size={16} />
              {t('workshop2.activity1.categoryManager.save')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CategoryEditorModal;
